/**
 * Media API - Favorites
 *
 * Add, remove, and list favorite media endpoints.
 */

import { z } from 'zod'
import { MediaTypeSchema, MediaVisibilitySchema } from './common'

// ============================================================================
// POST /v1/media/:id/favorite (Add Favorite)
// ============================================================================

export const MediaAddFavoriteRequestSchema = z.object({
  id: z.string().min(1)
})

export type MediaAddFavoriteRequest = z.infer<typeof MediaAddFavoriteRequestSchema>

export const MediaAddFavoriteResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    mediaId: z.string(),
    favoritedAt: z.coerce.date()
  })
})

export type MediaAddFavoriteResponse = z.infer<typeof MediaAddFavoriteResponseSchema>

// ============================================================================
// DELETE /v1/media/:id/favorite (Remove Favorite)
// ============================================================================

export const MediaRemoveFavoriteRequestSchema = z.object({
  id: z.string().min(1)
})

export type MediaRemoveFavoriteRequest = z.infer<typeof MediaRemoveFavoriteRequestSchema>

export const MediaRemoveFavoriteResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    mediaId: z.string()
  })
})

export type MediaRemoveFavoriteResponse = z.infer<typeof MediaRemoveFavoriteResponseSchema>

// ============================================================================
// GET /v1/media/favorites (List Favorites)
// ============================================================================

export const ListFavoritesRequestSchema = z.object({
  limit: z.coerce.number().min(1).max(100).optional(),
  offset: z.coerce.number().min(0).optional()
})

export type ListFavoritesRequest = z.infer<typeof ListFavoritesRequestSchema>

export const FavoriteMediaItemSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  mediaType: MediaTypeSchema,
  thumbnailUrl: z.string().nullable(),
  visibility: MediaVisibilitySchema,
  capturedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  favoritedAt: z.coerce.date()
})

export type FavoriteMediaItem = z.infer<typeof FavoriteMediaItemSchema>

export const ListFavoritesResponseSchema = z.object({
  message: z.string(),
  data: z.array(FavoriteMediaItemSchema),
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number()
  })
})

export type ListFavoritesResponse = z.infer<typeof ListFavoritesResponseSchema>
